import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "antd/dist/antd.css";
import { Select } from "antd";
import { HiOutlineChevronDown, HiOutlineSearch } from "react-icons/hi";

function normalizeItems(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
}

function getOptionValue(item, index, idKey, titleKey) {
  const id = item?.[idKey];
  if (id != null && id !== "") {
    return String(id);
  }

  const title = item?.[titleKey];
  if (title != null && title !== "") {
    return String(title);
  }

  return `option-${index}`;
}

function hasSelectedValue(selectedValue, idKey, titleKey) {
  if (!selectedValue || typeof selectedValue !== "object") {
    return false;
  }

  const id = selectedValue[idKey];
  const title = selectedValue[titleKey];

  return (id != null && id !== "") || (title != null && title !== "");
}

function getOptionLabel(item, titleKey, formatLabel) {
  if (typeof formatLabel === "function") {
    const custom = formatLabel(item);
    if (custom != null && String(custom).trim() !== "") {
      return String(custom);
    }
  }

  const label = item?.[titleKey];
  return label == null ? "" : String(label);
}

function Dropdown(props) {
  const {
    label,
    data,
    onSelect,
    titleKey,
    idKey,
    disabled,
    loading,
    selectedValue,
    dropdownClassName,
    listHeight = 260,
    required,
    invalid,
    errorText,
    formatLabel,
  } = props;
  const { Option } = Select;
  const titleKey_ = titleKey || "title";
  const idKey_ = idKey || "id";
  const items = useMemo(() => normalizeItems(data), [data]);
  const selectRef = useRef(null);
  const fieldRef = useRef(null);
  const searchInputRef = useRef(null);
  const autoSelectedRef = useRef(false);
  // Guards against Ant Select firing open twice (mousedown + focus),
  // and against an immediate spurious close right after open.
  const openLockRef = useRef(false);
  const openedAtRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectedOptionValue = useMemo(() => {
    if (!hasSelectedValue(selectedValue, idKey_, titleKey_)) {
      return undefined;
    }

    const selectedId = selectedValue[idKey_];
    if (selectedId != null && selectedId !== "") {
      const byId = items.find(
        (entry) => String(entry[idKey_]) === String(selectedId)
      );
      if (byId) {
        return getOptionValue(byId, items.indexOf(byId), idKey_, titleKey_);
      }
    }

    const selectedTitle = selectedValue[titleKey_];
    if (selectedTitle != null && selectedTitle !== "") {
      const byTitle = items.find(
        (entry) => String(entry[titleKey_]) === String(selectedTitle)
      );
      if (byTitle) {
        return getOptionValue(byTitle, items.indexOf(byTitle), idKey_, titleKey_);
      }
      return String(selectedTitle);
    }

    return undefined;
  }, [items, selectedValue, idKey_, titleKey_]);

  const filteredItems = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter((item) =>
      getOptionLabel(item, titleKey_, formatLabel).toLowerCase().includes(query)
    );
  }, [items, searchValue, titleKey_, formatLabel]);

  const onSelectHandler = useCallback(
    (value) => {
      if (value == null || value === "") {
        return;
      }

      const item =
        items.find(
          (entry, index) =>
            getOptionValue(entry, index, idKey_, titleKey_) === String(value)
        ) ||
        items.find((entry) => String(entry[titleKey_]) === String(value)) ||
        items.find((entry) => String(entry[idKey_]) === String(value));

      if (item) {
        onSelect(item);
      }

      setSearchValue("");
      openLockRef.current = false;
      setIsOpen(false);
    },
    [items, idKey_, titleKey_, onSelect]
  );

  const handleDropdownVisibleChange = useCallback(
    (open) => {
      // Keep closed while loading/disabled — avoids open→disable→reopen flicker.
      if (open && (disabled || loading)) {
        openLockRef.current = false;
        setIsOpen(false);
        return;
      }

      if (open) {
        // mousedown + focus often both request open; ignore the duplicate.
        if (openLockRef.current) return;
        openLockRef.current = true;
        openedAtRef.current = Date.now();
        setIsOpen(true);
        return;
      }

      // Ignore close events that fire in the same click/focus turn as open.
      if (Date.now() - openedAtRef.current < 180) {
        return;
      }

      openLockRef.current = false;
      setIsOpen(false);
      setSearchValue("");
    },
    [disabled, loading]
  );

  useEffect(() => {
    if (loading || disabled) {
      openLockRef.current = false;
      setIsOpen(false);
    }
  }, [loading, disabled]);

  useEffect(() => {
    if (loading || disabled) return;
    if (items.length !== 1 || selectedOptionValue) {
      if (items.length !== 1) autoSelectedRef.current = false;
      return;
    }
    if (autoSelectedRef.current) return;
    autoSelectedRef.current = true;
    onSelect(items[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedOptionValue, loading, disabled]);

  const preventSelectBlur = useCallback((event) => {
    // Stops Ant Select from closing when interacting with the custom search UI.
    event.preventDefault();
  }, []);

  const onSearchChange = useCallback((event) => {
    setSearchValue(event.target.value);
  }, []);

  const dropdownRender = useCallback(
    (menu) => (
      <div className="pi-form-dropdown__panel" onMouseDown={preventSelectBlur}>
        <div className="pi-form-dropdown__search">
          <HiOutlineSearch size={18} aria-hidden />
          <input
            ref={searchInputRef}
            type="search"
            className="pi-form-dropdown__search-input"
            value={searchValue}
            placeholder={`Search ${String(label || "options").toLowerCase()}...`}
            onChange={onSearchChange}
            onKeyDown={(event) => event.stopPropagation()}
            aria-label={`Search ${label}`}
          />
        </div>
        {loading ? (
          <div
            className="pi-form-dropdown__empty pi-form-dropdown__empty--loading"
            role="status"
          >
            <span className="pi-form-dropdown__spinner" aria-hidden />
            Loading options…
          </div>
        ) : items.length === 0 ? (
          <div className="pi-form-dropdown__empty">No options available</div>
        ) : filteredItems.length === 0 ? (
          <div className="pi-form-dropdown__empty">
            {searchValue.trim()
              ? `No results for “${searchValue.trim()}”`
              : "No options available"}
          </div>
        ) : (
          <div className="pi-form-dropdown__list">{menu}</div>
        )}
      </div>
    ),
    [
      filteredItems.length,
      items.length,
      label,
      loading,
      onSearchChange,
      preventSelectBlur,
      searchValue,
    ]
  );

  const optionNodes = useMemo(
    () =>
      filteredItems.map((item, index) => {
        const optionValue = getOptionValue(item, index, idKey_, titleKey_);
        const optionLabel = getOptionLabel(item, titleKey_, formatLabel);
        const company =
          item?.company_name ||
          item?.Company_name ||
          item?.companyName ||
          item?.dealer_company_name ||
          item?.dealerInfo?.companyName ||
          item?.dealerInfo?.company_name ||
          item?.dealer_info?.companyName ||
          "";
        const dealerName =
          item?.dealerInfo?.dealerName ||
          item?.dealerInfo?.dealer_name ||
          item?.dealer_info?.dealerName ||
          "";
        const address =
          item?.address || item?.Address || item?.Area || item?.area || "";
        const companyLine = [company, dealerName].filter(Boolean).join(" · ");
        const showSplit = Boolean(companyLine) && Boolean(address);

        return (
          <Option key={optionValue} value={optionValue} label={optionLabel}>
            {showSplit ? (
              <span className="pi-form-dropdown__option pi-form-dropdown__option--stacked">
                <span className="pi-form-dropdown__option-main">{address}</span>
                <span className="pi-form-dropdown__option-sub">{companyLine}</span>
              </span>
            ) : (
              <span className="pi-form-dropdown__option">{optionLabel}</span>
            )}
          </Option>
        );
      }),
    [Option, filteredItems, idKey_, titleKey_, formatLabel]
  );

  const fieldClassName = [
    "dropdown_wrapper",
    "pi-form-field",
    "pi-form-field--select",
    isOpen ? "is-open" : "",
    disabled ? "is-disabled" : "",
    loading ? "is-loading" : "",
    invalid ? "is-invalid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const selectClassName = [
    "pi-form-select",
    isOpen ? "is-open" : "",
    disabled ? "is-disabled" : "",
    loading ? "is-loading" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Portal to body so toggling field z-index (.is-open) does not remount the popup.
  const getPopupContainer = useCallback(() => document.body, []);

  return (
    <div ref={fieldRef} className={fieldClassName}>
      <span className="pi-form-field__label">
        {label}
        {required ? (
          <span className="pi-form-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </span>
      <div className="pi-form-select-shell">
        <Select
          ref={selectRef}
          className={selectClassName}
          size="large"
          loading={Boolean(loading)}
          disabled={Boolean(disabled || loading)}
          open={isOpen}
          showSearch={false}
          optionLabelProp="label"
          listHeight={listHeight}
          virtual={false}
          transitionName=""
          dropdownMatchSelectWidth
          dropdownClassName={["pi-form-dropdown", dropdownClassName || ""]
            .filter(Boolean)
            .join(" ")}
          dropdownStyle={{ zIndex: 12120 }}
          dropdownRender={dropdownRender}
          getPopupContainer={getPopupContainer}
          style={{ width: "100%" }}
          placeholder={
            loading
              ? `Loading ${String(label || "options").toLowerCase()}...`
              : `Choose ${String(label || "option").toLowerCase()}`
          }
          suffixIcon={
            loading ? (
              <span className="pi-form-dropdown__spinner" aria-hidden />
            ) : (
              <span className="pi-form-select__chevron" aria-hidden>
                <HiOutlineChevronDown size={18} />
              </span>
            )
          }
          onChange={onSelectHandler}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          value={selectedOptionValue}
          aria-required={required ? "true" : undefined}
          aria-invalid={invalid ? "true" : undefined}
        >
          {optionNodes}
        </Select>
      </div>
      {invalid && errorText ? (
        <p className="pi-form-field__error" role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

export default React.memo(Dropdown);
